import React, { useEffect, useRef } from "react";

import {
    RichTextStreamBlock as RichTextValue,
    TwoUpCardBlock as Value,
} from "@reactivated";

import { Themed } from "../../styles/themes";
import { delegateClickToLink } from "../../utils/delegateClickToLink";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichTextStreamBlock } from "../RichTextStreamBlock";

import styles from "./TwoUpCard.module.scss";

/**
 * Watch an element for content overflow via ResizeObserver
 * Reports overflow as a ratio: <=1 fits, >1 is overflowing
 */
export const useOverflow = (onOverflowChange: (ratio: number) => void) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = ref.current;
        /* v8 ignore if -- @preserve */
        if (!element) return;

        const check = () => {
            const ratio =
                element.clientHeight > 0
                    ? element.scrollHeight / element.clientHeight
                    : 1;
            onOverflowChange(ratio);
        };
        check();

        const observer = new ResizeObserver(check);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return ref;
};

const TwoUpCardContent = ({
    contentBlock,
    maxRatio,
    onOverflowChange,
    footer,
}: {
    contentBlock: RichTextValue;
    maxRatio: number;
    onOverflowChange: (ratio: number) => void;
    footer?: React.ReactNode;
}) => {
    // Make UI adjustments based on overflow ratio
    // Possibly-bad assumptions baked in about the general shape of content
    const hideBody = maxRatio > 1;
    const smallerHeading = maxRatio > 1.25;
    const clampHeading = maxRatio > 1.4;

    /** Render invisible version of the richtext for measurement */
    const measureRef = useOverflow(onOverflowChange);

    const { content, size_adjustment, ...otherStuff } = contentBlock;

    /**
     * Clamp header if there's WAAAY too much content
     */
    useEffect(() => {
        const id = content!.find((block) => block.type === "heading")?.id;
        const element = document.getElementById(id ?? "");
        element?.classList.toggle(styles.clampedHeading, clampHeading);
    }, [clampHeading]);

    /**
     * Filter out body blocks if there's too much content
     */
    const displayValue = {
        ...otherStuff,
        size_adjustment: smallerHeading ? "-1" : size_adjustment,
        content: hideBody
            ? content!.filter((block) => block.type !== "body")
            : content,
    };

    return (
        <Themed theme="darkThemeText">
            <div className={styles.content}>
                <RichTextStreamBlock value={displayValue} />
                {footer}
            </div>
            <div ref={measureRef} className={styles.measure} aria-hidden="true">
                <RichTextStreamBlock
                    value={{
                        ...contentBlock,
                        content: content?.map((block) => ({
                            ...block,
                            id: `measure-${block.id}`,
                        })),
                    }}
                />
            </div>
        </Themed>
    );
};

interface Props {
    value: Value;
    maxRatio: number;
    onOverflowChange: (ratio: number) => void;
    /** Content rendered after the rich text in the content overlay. */
    footer?: React.ReactNode;
}

export const TwoUpCard = ({
    value,
    maxRatio,
    onOverflowChange,
    footer,
}: Props) => {
    if (!value.media) return <></>;

    const contentBlock = value.content ?? null;
    const hasContent = contentBlock?.content && contentBlock.content.length > 0;

    return (
        <div className={styles.root} onClick={delegateClickToLink}>
            <MediaChooserBlock
                value={value.media}
                wrapperAttrs={{
                    className: hasContent
                        ? styles.mediaWithOverlay
                        : styles.media,
                }}
                imageProps={{ sizes: styles.sizes }}
            />
            {hasContent && (
                <TwoUpCardContent
                    contentBlock={contentBlock}
                    maxRatio={maxRatio}
                    onOverflowChange={onOverflowChange}
                    footer={footer}
                />
            )}
        </div>
    );
};
